import {
  computed,
  onScopeDispose,
  readonly,
  ref,
  watch,
  type ComputedRef,
  type Ref,
} from 'vue'
import { useHttp } from '@/shared/lib'
import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'
import type { ObjectTypeGroup } from '@/entities/Inventory/ObjectSubTypes'
import {
  computeNestedSkipBranchTypeIds,
  fetchListTypesBranchesForRoot,
  type ListTypesBranch,
} from './listTypesBranches'

/** Trailing debounce so the sidebar loader does not flicker between sequential parent fetches. */
const PARENT_LOADER_HIDE_DEBOUNCE_MS = 280

/** One «Справочники» accordion row (recursive `list_types` branches). */
export type ParentAccordion = ListTypesBranch

/**
 * Loads sidebar accordions for each `list_types` field on the type: fetches linked type fields
 * recursively (max depth 3) via the normalized fields API.
 */
export function useParentAccordions(
  ownFields: Ref<ObjectInventoryField[]>,
  groupId: Ref<string>,
  /** Catalog type id of the passport being edited (root hop `sourceOt`). */
  editedObjectTypeId: Ref<number>,
  fieldGroups: Ref<ObjectTypeGroup[]>,
  /**
   * Passport runtime needs nested `list_types` field metadata even when builder skips those branches
   * (nested skip hides LIST-linked types). Builder callers omit this (default false).
   */
  options?: {
    disableNestedSkip?: boolean
    /**
     * Явный список корневых `list_types` для загрузки (runtime: только ячейки раскладки).
     * Если не задан — прежнее поведение: все `list_types` поля типа.
     */
    listTypesRoots?: Ref<ObjectInventoryField[]>
    /** Стабильный ключ входов; при задании заменяет watch по `ownFields`. */
    inputsFingerprint?: Ref<string>
    /** Когда `false` — не дергать `/object-types/:id/fields` (нормализованный runtime). */
    enabled?: Ref<boolean>
  },
): {
  parentAccordions: ComputedRef<ParentAccordion[]>
  /** True while any accordion row is actively fetching (no debounce). */
  isLoading: ComputedRef<boolean>
  /** Debounced for UI: stays on during short gaps between parent loads. */
  isLoadingDebounced: Readonly<Ref<boolean>>
} {
  const http = useHttp()
  const list = ref<ParentAccordion[]>([])
  /**
   * In-flight root fetches. `list.value` is only assigned after the whole recursive walk finishes,
   * so `list.value.some(a => a.isLoading)` is `false` while branches are still loading. This counter
   * keeps `isLoading` (and downstream `isRuntimeMetadataReady` gates) truthful during that window —
   * otherwise the passport runtime captures its dirty-check snapshot before parent-accordion metadata
   * arrives and then flips `isDirty=true` once it does.
   */
  const pendingRootFetches = ref(0)
  let fetchGeneration = 0

  const rawAnyLoading = computed(
    () => pendingRootFetches.value > 0 || list.value.some((a) => a.isLoading),
  )
  const loadingDebounced = ref(false)
  let hideLoaderTimer: ReturnType<typeof setTimeout> | null = null

  watch(
    rawAnyLoading,
    (loading) => {
      if (loading) {
        if (hideLoaderTimer != null) {
          clearTimeout(hideLoaderTimer)
          hideLoaderTimer = null
        }
        loadingDebounced.value = true
        return
      }
      if (hideLoaderTimer != null) clearTimeout(hideLoaderTimer)
      hideLoaderTimer = setTimeout(() => {
        loadingDebounced.value = false
        hideLoaderTimer = null
      }, PARENT_LOADER_HIDE_DEBOUNCE_MS)
    },
    { immediate: true },
  )

  onScopeDispose(() => {
    if (hideLoaderTimer != null) clearTimeout(hideLoaderTimer)
  })

  watch(
    () =>
      options?.enabled?.value === false
        ? 'disabled'
        : (options?.inputsFingerprint?.value ??
          ([
        ownFields.value
          .filter((f) => f.type === 'list_types' && Number(f.object_type) > 0)
          .map((f) => `${String(f.id)}:${Number(f.object_type)}`)
          .sort()
          .join('|'),
        groupId.value,
        editedObjectTypeId.value,
        fieldGroups.value
          .map((g) => `${g.id ?? 0}:${g.type ?? ''}:${g.list_invent_object_type_id ?? 0}`)
          .sort()
          .join('|'),
      ] as const)),
    async () => {
      if (options?.enabled?.value === false) {
        list.value = []
        return
      }
      const myGen = ++fetchGeneration
      const gid = groupId.value
      const editedOt = editedObjectTypeId.value
      if (!gid || editedOt <= 0) {
        list.value = []
        return
      }

      const nestedSkip = options?.disableNestedSkip
        ? new Set<number>()
        : computeNestedSkipBranchTypeIds(editedOt, fieldGroups.value)

      const roots = options?.listTypesRoots?.value?.length
        ? options.listTypesRoots.value
        : ownFields.value.filter((f) => f.type === 'list_types' && Number(f.object_type) > 0)

      if (!roots.length) {
        list.value = []
        return
      }

      const accs: ParentAccordion[] = []

      // Hold the loading flag for the entire multi-root walk. A per-root increment/decrement would
      // drop `isLoading` to false between roots, briefly making the passport runtime think metadata
      // is ready and letting it capture a dirty-check snapshot against partial metadata.
      pendingRootFetches.value++
      try {
        for (const r of roots) {
          if (myGen !== fetchGeneration) return
          const part = await fetchListTypesBranchesForRoot(
            http,
            gid,
            editedOt,
            Number(r.object_type),
            r.id,
            r.name,
            `[${r.name}]`,
            () => myGen !== fetchGeneration,
            nestedSkip,
          )
          if (myGen !== fetchGeneration) return
          accs.push(...part)
          // Publish progressively so `parentAccordions`/metadata downstream recomputes during the
          // walk; the loading flag stays up until every root has finished.
          if (myGen === fetchGeneration) list.value = accs
        }
      } finally {
        // Always decrement to balance the `++` above. The generation check guards stale assignments
        // elsewhere; here we only restore the counter so it never leaks on supersession.
        pendingRootFetches.value--
      }
    },
    { immediate: true },
  )

  return {
    parentAccordions: computed(() => list.value),
    isLoading: rawAnyLoading,
    isLoadingDebounced: readonly(loadingDebounced),
  }
}
